/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import error from '../../../../common/nodejs/errors';
import config from '../../../../share/config';
import session from '../../../../share/session';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  SUCCESS: 'success',
  FAILED: 'failed',
  ERROR: 'error'
};

/** API type定義 */
const API_TYPE = {
  START: 'start',
  TERMINATE: 'terminate',
};

export default {
  name: 'requirement080',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      modal_requirement_080: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_080 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_080 = false;
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー画面の場合は、ウィザードを閉じる
      if (this.phase === PHASE.ERROR) {
        this.closeWizard();
        return;
      }
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 開始処理 */
    async doStart() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.START
        };

        // start API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              switch (response.data.async_code) {
                case error.WSP_080_INITIALIZATION_FAILED:
                  // 独自エラー
                  this.phase = PHASE.FAILED;
                  break;
                default:
                  // 共通エラー
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async doTerminate() {
      try {
        this.isLoading = true;

        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE
        };

        // terminate API実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
              if (this.phase === PHASE.ERROR) {
                // 共通エラー画面の場合は、ウィザードを閉じる
                this.closeWizard();
              } else {
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                switch (response.data.async_code) {
                  case error.WSP_080_INITIALIZATION_FAILED:
                    // 独自エラー
                    this.closeWizard();
                    break;
                  default:
                    // 共通エラー
                    this.showUnexpectedError();
                    break;
                }
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ユーザ操作
    /** 初期画面 OKボタン押下 */
    onStartOkClicked() {
      this.doStart();
    },

    /** 初期画面 キャンセルボタン押下 */
    onStartCancelClicked() {
      this.closeWizard();
    },

    /** 正常終了画面 OKボタン押下 */
    onSuccessOkClicked() {
      this.doTerminate();
    },

    /** 失敗表示画面 OKボタン押下 */
    onFailedOkClicked() {
      this.doTerminate();
    },

    /** 共通エラー画面 OKボタン押下 */
    onErrorOkClicked() {
      this.doTerminate();
    }
  },

  async mounted() {
    this.init();
  }
};
